import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Truck, Clock, Package } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import useSEO from "../hooks/useSEO";

/**
 * Zones desservies — page recentrée sur la valeur (qualité, délais, exemples)
 * plutôt que sur une liste exhaustive de villes (qui serait perçue comme
 * du keyword stuffing par Google).
 */
const REGIONS = [
  {
    name: "Auvergne-Rhône-Alpes",
    highlight: "Notre région d'origine",
    intro: "Basé à Moidieu-Détourbe (38), à 15 min de Vienne et 40 min de Lyon, notre atelier accueille de nombreux clients de la région sur rendez-vous. Retrait direct possible, livraison locale en 24 h.",
    cities: ["Lyon", "Grenoble", "Saint-Étienne", "Annecy", "Chambéry", "Valence", "Clermont-Ferrand", "Villeurbanne"],
    examples: "Mariages en Isère, coffrets d'entreprise à Lyon, trophées pour événements sportifs annéciens.",
  },
  {
    name: "Île-de-France & Grand Paris",
    highlight: "Livraison express 48 h",
    intro: "Nombreux clients professionnels à Paris et en petite couronne. Expédition Chronopost 24 h disponible pour les commandes urgentes.",
    cities: ["Paris", "Versailles", "Boulogne", "Neuilly", "Saint-Denis", "Créteil"],
    examples: "Cadeaux d'entreprise pour cabinets d'avocats parisiens, plaques signalétiques, coffrets sommelier.",
  },
  {
    name: "PACA & Côte d'Azur",
    highlight: "Spécialistes mariages et hôtellerie",
    intro: "Beaucoup de mariages haut de gamme et de commandes hôtelières entre Marseille, Cannes et Nice. Nous nous adaptons aux échéances événementielles.",
    cities: ["Marseille", "Nice", "Cannes", "Aix-en-Provence", "Toulon", "Antibes", "Saint-Tropez"],
    examples: "Flûtes personnalisées pour cérémonies au Cap-Ferrat, cadeaux VIP pour hôtels 5 étoiles cannois.",
  },
  {
    name: "Sud & Occitanie",
    highlight: "Domaines viticoles & artisanat local",
    intro: "Nous travaillons régulièrement avec des domaines viticoles et des restaurants gastronomiques du Sud-Ouest pour des bouteilles gravées et des cadeaux clients.",
    cities: ["Toulouse", "Montpellier", "Nîmes", "Perpignan", "Béziers", "Avignon"],
    examples: "Bouteilles millésimées gravées, sets de sommelier pour caves indépendantes, décorations pour mas provençaux.",
  },
  {
    name: "Nouvelle-Aquitaine",
    highlight: "Bordeaux, océan et gastronomie",
    intro: "Expédition dans toute la région, avec un fort volume à Bordeaux et sur la côte basque.",
    cities: ["Bordeaux", "Biarritz", "Bayonne", "La Rochelle", "Poitiers", "Limoges"],
    examples: "Coffrets vin gravés pour châteaux bordelais, trophées de compétitions à Biarritz.",
  },
  {
    name: "Grand Est",
    highlight: "Traditions champenoises et alsaciennes",
    intro: "Prestations régulières pour la Champagne et l'Alsace : gravure sur flûtes et coffrets, plaques en bois pour caveaux et winstubs.",
    cities: ["Strasbourg", "Reims", "Metz", "Nancy", "Mulhouse", "Colmar"],
    examples: "Flûtes gravées pour maisons de champagne, décorations gravées pour restaurants alsaciens.",
  },
  {
    name: "Hauts-de-France & Normandie",
    highlight: "Événementiel et culture",
    intro: "Beaucoup de commandes pour salons, événements et boutiques de créateurs.",
    cities: ["Lille", "Amiens", "Rouen", "Caen", "Le Havre"],
    examples: "Trophées d'événements professionnels lillois, décoration murale pour maisons normandes.",
  },
  {
    name: "Bretagne, Pays de la Loire & Centre",
    highlight: "Océan Atlantique et Val de Loire",
    intro: "Livraison sous 3-4 jours ouvrés partout dans l'Ouest.",
    cities: ["Nantes", "Rennes", "Brest", "Angers", "Tours", "Orléans", "Le Mans", "Saint-Malo"],
    examples: "Cadeaux invités pour mariages bretons, plaques d'accueil pour chambres d'hôtes en Val de Loire.",
  },
  {
    name: "Bourgogne-Franche-Comté & Corse",
    highlight: "Vignobles & îles",
    intro: "De Beaune à Ajaccio, nos créations voyagent aussi bien. Envoi sécurisé assuré pour la Corse (délai 4-5 jours).",
    cities: ["Dijon", "Besançon", "Beaune", "Ajaccio", "Bastia"],
    examples: "Verres gravés pour domaines bourguignons, cadeaux d'accueil pour maisons corses.",
  },
];

const ZonesPage = () => {
  useSEO({
    title: "Livraison partout en France",
    description: "Passion Verre livre partout en France sous 2 à 5 jours ouvrés. Retrait à Moidieu (38), expédition Colissimo ou Chronopost. Devis rapide sous 48 h.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Gravure et découpe laser sur mesure",
      "provider": { "@type": "Organization", "name": "Passion Verre", "url": "https://passionverre.com" },
      "areaServed": "FR",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Réalisations sur mesure — livraison France",
      },
    },
  });

  return (
    <div data-testid="zones-page">
      {/* HERO */}
      <section className="pt-32 pb-12 bg-[#171717]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Ornament />
          <SectionOverline className="mt-6">Livraison partout en France</SectionOverline>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-8 leading-tight">
            Vos créations, où que vous soyez.
          </h1>
          <p className="text-base sm:text-lg text-[#DDD4B2]/85 max-w-2xl mx-auto leading-relaxed">
            Basé en Isère à mi-chemin entre Lyon et Vienne, notre atelier expédie
            partout en France métropolitaine et en Corse. Retrait sur place possible,
            livraison sécurisée par Colissimo ou Chronopost selon vos délais.
          </p>
        </div>
      </section>

      {/* PROMISES */}
      <section className="py-16 bg-[#0f0f0f] border-y border-[#9A8640]/20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {[
            { icon: Clock, title: "Devis sous 48h", text: "Envoyez votre projet, nous revenons vers vous avec une proposition claire, chiffrée et faisable." },
            { icon: Truck, title: "Livraison 2-5 jours", text: "Colissimo, Chronopost ou transporteur pour les gros volumes — vous choisissez selon l'urgence." },
            { icon: Package, title: "Emballage soigné", text: "Chaque commande est emballée avec précaution : caisse renforcée pour le verre, mousse pour les objets fragiles." },
          ].map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center"
            >
              <div className="w-14 h-14 rounded-full bg-[#9A8640]/15 border border-[#9A8640]/40 flex items-center justify-center mx-auto mb-4">
                <p.icon className="text-[#9A8640]" size={22} />
              </div>
              <h3 className="font-serif text-2xl text-[#DDD4B2] mb-2">{p.title}</h3>
              <p className="text-sm text-[#DDD4B2]/75 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* REGIONS */}
      <section className="py-20 bg-[#171717]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionOverline>Zones d'intervention</SectionOverline>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#DDD4B2] mt-3">Ils nous font confiance dans toute la France</h2>
            <p className="text-sm text-[#DDD4B2]/70 mt-4 max-w-2xl mx-auto">
              Voici quelques régions et types de projets que nous avons régulièrement l'occasion de servir.
              Cette liste n'est pas exhaustive : nous livrons dans toutes les communes de France.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {REGIONS.map((r, i) => (
              <motion.article
                key={r.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                className="border border-[#9A8640]/25 p-7 hover:border-[#9A8640]/60 transition-colors"
                data-testid={`region-card-${i}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-[#9A8640]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#9A8640]">{r.highlight}</span>
                </div>
                <h3 className="font-serif text-2xl text-white mb-3">{r.name}</h3>
                <p className="text-sm text-[#DDD4B2]/85 leading-relaxed mb-4">
                  {r.intro}
                </p>
                <p className="text-xs text-[#DDD4B2]/60 mb-4">
                  <span className="text-[#9A8640] uppercase tracking-[0.15em] text-[10px]">Principales villes desservies : </span>
                  {r.cities.join(", ")}.
                </p>
                <p className="text-sm text-[#DDD4B2]/70 italic border-l-2 border-[#9A8640]/40 pl-4">
                  {r.examples}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Ornament className="mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl mb-6">Votre projet démarre ici</h2>
          <p className="text-base sm:text-lg leading-relaxed text-[#171717]/85 mb-10">
            Quelle que soit votre ville, nous vous accompagnons de A à Z :
            conseil, design, prototype avant validation, production en atelier
            et livraison chez vous. Chaque commande est traitée personnellement,
            jamais standardisée.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#171717] text-[#DDD4B2] px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-all"
            data-testid="zones-cta"
          >
            Recevoir un devis <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ZonesPage;
